import { Fill, Style, Text } from 'ol/style.js';
import { asString } from 'ol/color';
import crosshair from '@disy/cadenza-icons/crosshair.svg';
import Icon from 'ol/style/Icon';
import ImageState from 'ol/ImageState';

import type {
  CoordinateSketchStyle,
  ImageSketchProperties,
  ImageSketchStyle,
  LineStringSketchStyle,
  MeasureSketchProperties,
  PointSketchStyle,
  PolygonSketchStyle,
  SketchStyleProperties,
  SymbolSketchStyle,
  TextSketchStyle
} from 'cadenza/workbook/sketch/model/sketch-types';
import {
  getSketchItemDto,
  getSketchItemDtoWithGeometry,
  SketchItemType
} from 'cadenza/workbook/sketch/model/sketch-types';
import { fonts, toCssFontSize } from 'cadenza/utils/fonts';
import { createStyle, DEFAULT_OL_Z_INDEX } from 'cadenza/map/style/create-style';
import { AnchorType, LineDecoration, PointSignatureType, StyleProperty } from 'cadenza/map/style/style-constants';
import { GeometryType } from 'cadenza/map/geom/geometry-type';
import {
  createSketchFeatureStrategy,
  createSketchStrategy,
  createSketchTypeStrategy
} from 'cadenza/workbook/sketch/sketch-layer/sketch-strategy';
import { degreesToRadians } from 'cadenza/utils/math';
import type { RgbaColor } from 'cadenza/utils/colors/colors';
import { applyOpacityFactorToColor, getColorWithOpacity, toRgbaColor } from 'cadenza/utils/colors/colors';
import { getMediaItemDownloadUrl } from 'cadenza/api-client/repositories-media-api';
import { assert, assertType } from 'cadenza/utils/custom-error';
import { setupCoordinatesOverlay } from 'cadenza/workbook/sketch/interactions/capture-coordinates-interaction';
import { deserializeGeometry } from 'cadenza/workbook/sketch/sketch-layer/sketch-format';
import { parseSvg } from 'cadenza/utils/svg';
import type { Map } from 'cadenza/map/map';
import { getImageObjectRenderer } from 'cadenza/map/geom/image-object-renderer';
import { on } from 'cadenza/utils/event-util';
import type { SketchItemDtoWithGeometry } from 'cadenza/workbook/sketch/dto/sketch-item-dto';
import { ajax } from 'cadenza/api-client/api';
import { logger } from 'cadenza/utils/logging';
import { createMeasureDistanceStyle, setupDistanceMeasureOnFeature } from 'cadenza/map/interactions/measure-distance';
import { createMeasureAreaStyle, setupAreaMeasureOnFeature } from 'cadenza/map/interactions/measure-area';
import { clearMeasureOverlaysForFeature } from 'cadenza/map/interactions/measure';

import type LineString from 'ol/geom/LineString';
import type Polygon from 'ol/geom/Polygon';
import type { StyleLike } from 'ol/style/Style';
import type Feature from 'ol/Feature';

export type RenderingStrategy = (feature: Feature) => Promise<void | Style>;

export function getRenderingStrategy (map: Map) {
  return createSketchFeatureStrategy<Promise<Style | void>>({
    [SketchItemType.POINT]: feature => geometryRendering(feature),
    [SketchItemType.OVAL]: feature => ovalAndRectangleGeometryRendering(feature),
    [SketchItemType.RECTANGLE]: feature => ovalAndRectangleGeometryRendering(feature),
    [SketchItemType.POLYGON]: feature => geometryRendering(feature),
    [SketchItemType.LINE_STRING]: feature => geometryRendering(feature),
    [SketchItemType.DISTANCE_MEASUREMENT]: feature => distanceMeasureRendering(feature, map),
    [SketchItemType.AREA_MEASUREMENT]: feature => areaMeasureRendering(feature, map),
    [SketchItemType.COORDINATE]: feature => coordinatesRendering(feature, map),
    [SketchItemType.TEXT]: feature => geometryRendering(feature),
    [SketchItemType.IMAGE]: feature => imageGeometryRendering(feature),
    [SketchItemType.SYMBOL]: feature => imageGeometryRendering(feature)
  });
}

async function geometryRendering (feature: Feature) {
  const style = await itemToOpenLayersStyle(getSketchItemDto(feature));
  feature.setStyle(style);
  return style;
}

async function imageGeometryRendering (feature: Feature) {
  const sketchItemDto = getSketchItemDtoWithGeometry(feature) as SketchItemDtoWithGeometry<'Image' | 'Symbol'>;
  const style = await imageStyle(sketchItemDto.properties, sketchItemDto.zIndex);
  const geometryFromSketchItemDto = deserializeGeometry(sketchItemDto.type, sketchItemDto.geometry, { styles: sketchItemDto.styles });
  feature.setStyle(style);
  feature.setGeometry(geometryFromSketchItemDto);
  return style;
}

async function ovalAndRectangleGeometryRendering (feature: Feature) {
  const sketchItemDto = getSketchItemDtoWithGeometry(feature) as SketchItemDtoWithGeometry<'Oval' | 'Rectangle'>;
  const geometryFromSketchItemDto = deserializeGeometry(sketchItemDto.type, sketchItemDto.geometry, { styles: sketchItemDto.styles });
  const style = await itemToOpenLayersStyle(getSketchItemDto(feature));
  feature.setStyle(style);
  feature.setGeometry(geometryFromSketchItemDto);
  return style;
}

const itemToOpenLayersStyle = createSketchStrategy<Promise<Style>>({
  [SketchItemType.POINT]: item => Promise.resolve(pointStyleToOlStyle(item.styles, item.zIndex)),
  [SketchItemType.OVAL]: item => Promise.resolve(polygonStyleToOlStyle(item.styles, item.zIndex, GeometryType.OVAL)),
  [SketchItemType.RECTANGLE]: item => Promise.resolve(polygonStyleToOlStyle(item.styles, item.zIndex, GeometryType.RECTANGLE)),
  [SketchItemType.POLYGON]: item => Promise.resolve(polygonStyleToOlStyle(item.styles, item.zIndex, GeometryType.POLYGON)),
  [SketchItemType.LINE_STRING]: item => Promise.resolve(lineModelStyleToOlStyle(item.styles, item.zIndex)),
  [SketchItemType.DISTANCE_MEASUREMENT]: item => Promise.resolve(coordinateOrMeasureStyleToOlStyle(item.zIndex, 'DistanceMeasurement', GeometryType.LINE_STRING)),
  [SketchItemType.AREA_MEASUREMENT]: item => Promise.resolve(coordinateOrMeasureStyleToOlStyle(item.zIndex, 'AreaMeasurement', GeometryType.POLYGON)),
  [SketchItemType.COORDINATE]: item => Promise.resolve(coordinateOrMeasureStyleToOlStyle(item.zIndex, 'Coordinate', GeometryType.POINT)),
  [SketchItemType.TEXT]: item => Promise.resolve(textStyle(item.styles, item.zIndex)),
  [SketchItemType.IMAGE]: item => imageStyle(item.properties, item.zIndex),
  [SketchItemType.SYMBOL]: item => imageStyle(item.properties, item.zIndex)
});

function pointStyleToOlStyle (style: PointSketchStyle, zIndex: number) {
  const styleConfiguration = {
    [StyleProperty.POINT_SIGNATURE_TYPE]: style[StyleProperty.POINT_SIGNATURE_TYPE],
    [StyleProperty.FOREGROUND_COLOR]: style[StyleProperty.FOREGROUND_COLOR],
    [StyleProperty.SYMBOL_SIZE]: style[StyleProperty.SYMBOL_SIZE],
    [StyleProperty.LINE_WIDTH]: style[StyleProperty.LINE_WIDTH],
    [StyleProperty.BORDER_COLOR]: style[StyleProperty.BORDER_COLOR],
    [StyleProperty.Z_INDEX]: zIndex || DEFAULT_OL_Z_INDEX
  };
  return createStyle(styleConfiguration, { geometryType: GeometryType.POINT });
}

function coordinateOrMeasureStyleToOlStyle (zIndex: number, sketchItemType: SketchItemType, geometryType: GeometryType) {
  const styleConfiguration = {
    ...getDefaultStyleStrategy()(sketchItemType),
    [StyleProperty.Z_INDEX]: zIndex || DEFAULT_OL_Z_INDEX
  };
  return createStyle(styleConfiguration, { geometryType });
}

function polygonStyleToOlStyle (style: PolygonSketchStyle, zIndex: number, geometryType: GeometryType) {
  const styleConfiguration = {
    [StyleProperty.FOREGROUND_COLOR]: style[StyleProperty.FOREGROUND_COLOR],
    [StyleProperty.LINE_WIDTH]: style[StyleProperty.LINE_WIDTH],
    [StyleProperty.BORDER_COLOR]: style[StyleProperty.BORDER_COLOR],
    [StyleProperty.Z_INDEX]: zIndex || DEFAULT_OL_Z_INDEX
  };
  return createStyle(styleConfiguration, { geometryType });
}

function lineModelStyleToOlStyle (style: LineStringSketchStyle, zIndex: number) {
  const styleConfiguration = {
    [StyleProperty.FOREGROUND_COLOR]: style[StyleProperty.FOREGROUND_COLOR],
    [StyleProperty.LINE_WIDTH]: style[StyleProperty.LINE_WIDTH],
    [StyleProperty.LINE_START_DECORATION]: style[StyleProperty.LINE_START_DECORATION],
    [StyleProperty.LINE_END_DECORATION]: style[StyleProperty.LINE_END_DECORATION],
    [StyleProperty.Z_INDEX]: zIndex || DEFAULT_OL_Z_INDEX
  };

  return createStyle(styleConfiguration, { geometryType: GeometryType.LINE });
}

async function distanceMeasureRendering (feature: Feature, map: Map) {
  const style = createMeasureDistanceStyle(map);
  feature.setStyle(style);
  updateDistanceMeasureOverlays(feature, map);
  return Promise.resolve();
}

async function areaMeasureRendering (feature: Feature, map: Map) {
  const style = createMeasureAreaStyle(map);
  feature.setStyle(style);
  updateAreaMeasureOverlays(feature, map);
  return Promise.resolve();
}

async function coordinatesRendering (feature: Feature, map: Map) {
  const style = coordinateOrMeasureStyleToOlStyle(getSketchItemDto(feature).zIndex, 'Coordinate', GeometryType.POINT);
  feature.setStyle(style);
  setupCoordinatesOverlay(feature, map);
  return Promise.resolve();
}

export function textStyle (style: TextSketchStyle, zIndex?: number) {
  const [ textBaseline, textAlign ] = AnchorType[style[StyleProperty.ANCHOR]];
  let fillColor = style[StyleProperty.BACKGROUND_VISIBLE] ? style[StyleProperty.BACKGROUND_COLOR] : asString(getColorWithOpacity(style[StyleProperty.BACKGROUND_COLOR], 0));
  const rotation = style[StyleProperty.ROTATION] ? degreesToRadians(style[StyleProperty.ROTATION]) : undefined;
  return new Style({
    text: new Text({
      backgroundFill: new Fill({ color: fillColor }),
      font: getFontStyle(style),
      text: style.text ?? '',
      fill: new Fill({ color: style[StyleProperty.FOREGROUND_COLOR] }),
      textBaseline,
      textAlign,
      placement: 'point',
      rotation,
      padding: [ 2, 0, 2, 0 ]
    }),
    zIndex: zIndex ?? DEFAULT_OL_Z_INDEX
  });
}

function getFontStyle (style: TextSketchStyle) {
  const fontStyle = [];
  if (style.bold) {
    fontStyle.push('bold');
  }
  if (style.italic) {
    fontStyle.push('italic');
  }
  fontStyle.push(toCssFontSize(style.fontSize));
  fontStyle.push(style.fontFamily);
  return fontStyle.join(' ');
}

export function getTextContentFromFeatureStyle (style?: StyleLike) {
  // The text is set in sketch-draw-interaction::finishTextDraw
  assert(style instanceof Style, 'The style of text features is a simple style');
  let text = style.getText()!.getText();
  if (Array.isArray(text)) {
    text = text.join('\n');
  }
  return text;
}

export async function imageStyle (properties: ImageSketchProperties, zIndex: number) {
  const image = await loadImageFromProperties(properties);
  return getImageRendererStyle(image, { zIndex, keepAspectRatio: properties.keepAspectRatio });
}

export function getImageRendererStyle (image: HTMLImageElement | HTMLCanvasElement | ImageBitmap, { keepAspectRatio = true, zIndex }: {
  keepAspectRatio?: boolean;
  zIndex?: number;
}) {
  const renderer = getImageObjectRenderer(image, keepAspectRatio);
  return new Style({ renderer, zIndex });
}

export async function loadImageFromProperties (properties: ImageSketchProperties) {
  const imageSrc = getMediaItemDownloadUrl(properties);
  const loadedImage = await loadImage(imageSrc);
  // This is workaround of a bug in firefox that prevents adding svg files without width and height
  // to canvas. This results in svg images not visible in sketch layer. Bug description:
  // https://stackoverflow.com/questions/28690643/firefox-error-rendering-an-svg-image-to-html5-canvas-with-drawimage
  if (loadedImage instanceof HTMLImageElement && (!loadedImage.width || !loadedImage.height)) {
    return await getFixedSvgWithWidthAndHeight(imageSrc) ?? loadedImage;
  }
  return loadedImage;
}

function loadImage (src: string) {
  return new Promise<HTMLImageElement | HTMLCanvasElement | ImageBitmap>(resolve => {
    const icon = new Icon({ src });
    const image = icon.getImage(1);
    // Because of OL caching, the onload is not triggered if the image is already loaded
    if (icon.getImageState() === ImageState.LOADED || image instanceof ImageBitmap) {
      resolve(image);
    } else {
      // Do not use image.onload, because for the same image src, due to caching, the second call
      // will overwrite the event listener, and the Promise would never get resolved
      on(image, 'load', () => resolve(image), { once: true });
      icon.load();
    }
  });
}

function updateDistanceMeasureOverlays (feature: Feature, map: Map) {
  assertType<Feature<LineString>>(feature,
    feature.getGeometry()?.getType() === 'LineString',
    'Distance Measure feature must be a LineString.');
  clearMeasureOverlaysForFeature(feature);
  const unit = getUnitFromDtoProperties(feature);
  setupDistanceMeasureOnFeature(feature, map, unit);
}

function updateAreaMeasureOverlays (feature: Feature, map: Map) {
  assertType<Feature<Polygon>>(feature,
    feature.getGeometry()?.getType() === 'Polygon',
    'Area Measure feature must be a Polygon.');
  clearMeasureOverlaysForFeature(feature);
  const unit = getUnitFromDtoProperties(feature);
  setupAreaMeasureOnFeature(feature, map, unit);
}

function getUnitFromDtoProperties (feature: Feature) {
  const properties = getSketchItemDto(feature)?.properties;
  assertType<MeasureSketchProperties | undefined>(properties, !properties
      || getSketchItemDto(feature)?.type === SketchItemType.AREA_MEASUREMENT
      || getSketchItemDto(feature)?.type === SketchItemType.DISTANCE_MEASUREMENT);
  return properties?.unit ?? 'metric';
}

export function getDefaultStyleStrategy () {
  const primaryColor = toRgbaColor(window.Disy.theme['primary-color']);
  const borderColor = toRgbaColor(applyOpacityFactorToColor(primaryColor, 0.9));
  const fillColor = toRgbaColor(applyOpacityFactorToColor(primaryColor, 0.1));
  const defaultTextColor = 'rgba(0, 0, 0, 0.9)';
  const defaultTextBackground = 'rgba(255, 255, 255, 0.9)';
  const measureBorderColor = 'rgba(0, 0, 0, 0.9)';
  const lineWidth = 2;

  return createSketchTypeStrategy<SketchStyleProperties>({
    [SketchItemType.POINT]: () => getDefaultPointStyle(fillColor, borderColor, lineWidth),
    [SketchItemType.OVAL]: () => getDefaultPolygonStyle(fillColor, borderColor, lineWidth),
    [SketchItemType.RECTANGLE]: () => getDefaultPolygonStyle(fillColor, borderColor, lineWidth),
    [SketchItemType.POLYGON]: () => getDefaultPolygonStyle(fillColor, borderColor, lineWidth),
    [SketchItemType.LINE_STRING]: () => getDefaultLineStringStyle(fillColor, borderColor, lineWidth),
    [SketchItemType.DISTANCE_MEASUREMENT]: () => getDefaultMeasureDistanceStyle(fillColor, measureBorderColor, lineWidth),
    [SketchItemType.AREA_MEASUREMENT]: () => getDefaultMeasureAreaStyle(fillColor, measureBorderColor, lineWidth),
    [SketchItemType.COORDINATE]: () => getDefaultCoordinateStyle(),
    [SketchItemType.TEXT]: () => getDefaultTextStyle(defaultTextBackground, defaultTextColor),
    [SketchItemType.IMAGE]: () => getDefaultImageStyle(),
    [SketchItemType.SYMBOL]: () => getDefaultSymbolStyle()
  });
}

function getDefaultPointStyle (fillColor: RgbaColor, borderColor: RgbaColor, lineWidth: number): PointSketchStyle {
  return {
    [StyleProperty.POINT_SIGNATURE_TYPE]: PointSignatureType.CIRCLE,
    [StyleProperty.FOREGROUND_COLOR]: fillColor,
    [StyleProperty.SYMBOL_SIZE]: 10,
    [StyleProperty.LINE_WIDTH]: lineWidth,
    [StyleProperty.BORDER_COLOR]: borderColor
  };
}

function getDefaultCoordinateStyle (): CoordinateSketchStyle {
  return {
    [StyleProperty.POINT_SIGNATURE_TYPE]: PointSignatureType.IMAGE,
    [StyleProperty.SYMBOL_SIZE]: 1,
    [StyleProperty.IMAGE_AS_BASE64]: 'data:image/svg+xml;base64,' + window.btoa(parseSvg(crosshair).outerHTML)
  };
}

function getDefaultLineStringStyle (fillColor: RgbaColor, borderColor: RgbaColor, lineWidth: number): LineStringSketchStyle {
  return {
    [StyleProperty.FOREGROUND_COLOR]: borderColor,
    [StyleProperty.LINE_WIDTH]: lineWidth,
    [StyleProperty.LINE_START_DECORATION]: LineDecoration.NONE,
    [StyleProperty.LINE_END_DECORATION]: LineDecoration.NONE
  };
}

function getDefaultMeasureDistanceStyle (fillColor: RgbaColor, borderColor: RgbaColor, lineWidth: number): LineStringSketchStyle {
  return {
    ...getDefaultLineStringStyle(fillColor, borderColor, lineWidth),
    [StyleProperty.LINE_WIDTH]: 1,
    [StyleProperty.LINE_STYLE]: 'TinyDot'
  };
}

function getDefaultPolygonStyle (fillColor: RgbaColor, borderColor: RgbaColor, lineWidth: number): PolygonSketchStyle {
  return {
    [StyleProperty.FOREGROUND_COLOR]: fillColor,
    [StyleProperty.LINE_WIDTH]: lineWidth,
    [StyleProperty.BORDER_COLOR]: borderColor
  };
}

function getDefaultImageStyle (): ImageSketchStyle {
  return {
    [StyleProperty.ROTATION]: 0
  };
}

function getDefaultSymbolStyle (): SymbolSketchStyle {
  return {
    [StyleProperty.ROTATION]: 0
  };
}

function getDefaultMeasureAreaStyle (fillColor: RgbaColor, borderColor: RgbaColor, lineWidth: number): PolygonSketchStyle {
  return {
    ...getDefaultPolygonStyle(fillColor, borderColor, lineWidth),
    [StyleProperty.LINE_WIDTH]: 1,
    [StyleProperty.LINE_STYLE]: 'TinyDot'
  };
}

function getDefaultTextStyle (fillColor: RgbaColor, borderColor: RgbaColor): TextSketchStyle {
  return {
    [StyleProperty.FOREGROUND_COLOR]: borderColor,
    [StyleProperty.BACKGROUND_COLOR]: fillColor,
    [StyleProperty.ANCHOR]: 'WEST',
    [StyleProperty.BOLD]: false,
    [StyleProperty.ITALIC]: false,
    [StyleProperty.FONT_SIZE]: 14,
    [StyleProperty.FONT_FAMILY]: fonts['Arial'],
    [StyleProperty.ROTATION]: 0
  };
}

async function getFixedSvgWithWidthAndHeight (svgSrc: string) {
  // If svg image don't have width and height defined we have to re-download it as XML and set width and
  // height attributes calculated from svg viewBox. Then XML is serialized as base64 and appended to new
  // image as inline src.
  try {
    const svgXml = await getXMLImageFromUrl(svgSrc);
    const svgElement = svgXml.querySelector('svg');
    if (!svgElement) {
      logger.log('Problem with parsing provided sketch image.');
      return;
    }
    const { width, height } = svgElement.viewBox.baseVal;
    if (!width || !height) {
      logger.log('Problem with parsing provided sketch image.');
      return;
    }
    svgElement.setAttribute('width', width + 'px');
    svgElement.setAttribute('height', height + 'px');
    const serializedSvgWithFixedSize = new XMLSerializer().serializeToString(svgElement);
    const fixedSvg64 = btoa(serializedSvgWithFixedSize);
    const img = new Image();
    img.height = height;
    img.width = width;
    img.src = 'data:image/svg+xml;base64,' + fixedSvg64;
    return img;
  } catch {
    logger.log('Problem with parsing provided sketch image.');
    return;
  }
}

export function getXMLImageFromUrl (url: string) {
  return ajax<XMLDocument>(url, {
    type: 'GET',
    dataType: 'xml'
  });
}
